import { JSX } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import Button from "@/components/buttons/Button.tsx";
import Input from "@/components/inputs/Input.tsx";
import { MODAL_ROUTES } from "@/components/modals/router/ModalRoutes";

import { ROUTES } from "@/config/routes";
import { usePostLoginPassword } from "@/hooks/api/login.hooks";
import { useResetPassword } from "@/hooks/api/user.hooks";
import useModalRouter from "@/hooks/useModalRouter";
import { onError } from "@/utils/onError";
import { onSuccess } from "@/utils/onSuccess";

import { ForgotPasswordForm } from "./types";

const ForgotPasswordSetNew = (): JSX.Element => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    getValues,
  } = useFormContext<ForgotPasswordForm>();

  const { mutate: mutatePassword, isPending } = useResetPassword();
  const { goBack, openModal } = useModalRouter();
  const { mutate: passwordMutate } = usePostLoginPassword();
  const { closeModal } = useModalRouter();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const code = params.get("code");
  const account_id = params.get("account_id");

  if (!code || !account_id) return <></>;

  const onSubmit = async (data: ForgotPasswordForm) => {
    const formData = new FormData();

    if (data.confirm_password != data.new_password) {
      setError("confirm_password", { message: t("confirm-password-invalid") });

      return;
    }

    formData.append("password", data.new_password);
    formData.append("code", code);
    formData.append("account_id", account_id);
    const access = localStorage.getItem("splay:access");

    mutatePassword(
      { formData },
      {
        onSuccess: () => {
          onSuccess(t("change-password-success"));

          if (!access) {
            loginWithPassword();
            return;
          }

          openModal([MODAL_ROUTES.FORGOT_PASSWORD, MODAL_ROUTES.FORGOT_PASSWORD_SUCCESS], {}, true);
        },

        onError: (error) => {
          onError(error);
        },
      },
    );
  };

  const loginWithPassword = () => {
    passwordMutate(
      { username: getValues("login"), password: getValues("new_password") },
      {
        onSuccess: (res) => {
          const isActive = res.data.is_active;

          if (isActive) {
            closeModal();
            navigate(ROUTES.GATE.PROFILES);
            return;
          }

          openModal([MODAL_ROUTES.AUTH, MODAL_ROUTES.AUTH_SESSIONS], { token: res.data.token });
        },
      },
    );
  };

  return (
    <>
      <div className={"staff-modal__header"}>
        <h2 className={"staff-modal__title"}>{t("auth.new-password")}</h2>
        <p className={"staff-modal__description mb-8 text-center"}>{t("auth.new-password-message")}</p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className={"w-full space-y-10"}>
        <div className={"space-y-6"}>
          <Input
            type={"password"}
            color={"white"}
            placeholder={t("auth.create-new-password")}
            errorMessage={errors.new_password?.message}
            name={"new_password"}
            register={register}
            validation={{
              required: true,
            }}
            errors={errors}
            autoComplete={"new-password"}
          />
          <Input
            type={"password"}
            color={"white"}
            placeholder={t("auth.repeat-new-password")}
            name={"confirm_password"}
            errorMessage={errors.confirm_password?.message}
            register={register}
            validation={{
              required: true,
            }}
            errors={errors}
            autoComplete={"new-password"}
          />
        </div>
        <div className={"space-y-4"}>
          <Button type={"submit"} isLoading={isPending} title={t("Actions.save")}>
            {t("Actions.save")}
          </Button>
          <Button type={"button"} title={t("Actions.cancel")} onClick={goBack} color={"black"} className={"w-full"}>
            {t("Actions.cancel")}
          </Button>
        </div>
      </form>
    </>
  );
};

export default ForgotPasswordSetNew;
